import { ArrowTopRightIcon } from '@radix-ui/react-icons';

const Changelog = () => {
  const changelogEntries: {
    date: string;
    sections: {
      title: string;
      content: string;
      link?: { url: string; label: string };
    }[];
  }[] = [
    {
      date: "January 29, 2026",
      sections: [
        {
          title: "One-Click Deployment",
          content: "Deploy vLLM models with a single click. Choose from preset models or add custom HuggingFace models with automatic capability detection.\n\nKey features:\n• Preset models: Qwen, Llama, DeepSeek, Typhoon, and more\n• Custom model support with /model/support-matrix validation\n• Auto-detect vision, reasoning, and tool calling capabilities\n• Configure reasoning parser and tool parser for custom models\n• Minimum 100GB volume for model weights storage"
        },
        {
          title: "Endpoint Proxy",
          content: "Access any service running on your GPU instances via secure proxy endpoints. Works with both SSH (base-vm) and One-Click Deploy instances.\n\nEndpoint format:\n• https://proxy-instance.float16.cloud/{task_id}/{port}/{path}\n• Supports any HTTP service on ports 3000-4000\n• Works with vLLM, custom APIs, Jupyter, and more\n• Compatible with OpenAI Python SDK for vLLM endpoints"
        },
        {
          title: "vLLM Playground",
          content: "Interactive playground to test your deployed models with advanced features.\n\nFeatures:\n• Tool Calling - Test function calling with example tools (weather, calculator, search)\n• Structured Outputs - JSON Schema, Regex patterns, and Choice constraints\n• Typhoon OCR - Extract text from Thai/English documents with markdown output\n• View Code - Copy Python, cURL, or JSON examples for integration"
        }
      ]
    },
    {
      date: "January 25, 2026",
      sections: [
        {
          title: "GPU Instance - Stop/Resume Support",
          content: "GPU Instances now support lifecycle management with Stop, Resume, and Terminate actions.\n\nKey features:\n• Stop instance to pause compute and save costs\n• Resume instance to continue from where you left off\n• Terminate instance to permanently delete resources\n• Instance History showing all session records\n\nCost savings: During stopped state, only volume storage cost is charged - no instance compute cost. This allows you to preserve your data and environment while not actively using the instance."
        }
      ]
    },
    {
      date: "January 7, 2026",
      sections: [
        {
          title: "Payment Model Update",
          content: "We've simplified our payment system to a topup-only model. You can now add credit to your account and use it across all services."
        }
      ]
    },
    {
      date: "January 4, 2026",
      sections: [
        {
          title: "GPU Instance - Native SSH Connect",
          content: "Introducing GPU Instance, a new way to access dedicated GPU resources with native SSH connectivity. Create GPU instances on-demand and connect directly via SSH for full control over your development and training environment.\n\nKey features:\n• Dedicated GPU instances with SSH access\n• Auto-generated SSH keys for secure authentication\n• Flexible volume storage configuration\n• Multiple instance types available"
        },
        {
          title: "Serverless GPU v2",
          content: "We've upgraded Serverless GPU with significant improvements:\n\n• Streaming response in manual mode - Get real-time output as your code executes\n• More reliable - Enhanced stability and improved error handling for a smoother experience",
          link: { url: "https://docs.float16.cloud", label: "View Documentation" }
        },
        {
          title: "Storage Logs",
          content: "Track all storage operations for your Serverless GPU projects with the new Storage Logs feature.\n\nKey features:\n• View upload, download, delete, and other storage operations\n• Dashboard with operation statistics and 7-day activity chart\n• Filter logs by operation type, date range, and project\n• Available at both workspace and project level"
        }
      ]
    },
    {
      date: "December 30, 2025",
      sections: [
        {
          title: "VS Code Extension",
          content: "Introducing the Float16 CLI Tools extension for Visual Studio Code. Run and manage your Serverless GPU projects directly from VS Code with integrated authentication and project setup.",
          link: { url: "https://marketplace.visualstudio.com/items?itemName=float16.float16-cli-tools", label: "Install Extension" }
        }
      ]
    },
    {
      date: "December 25, 2025",
      sections: [
        {
          title: "Blueprint for Parabricks",
          content: "Added Parabricks blueprint to Serverless GPU. Easily run NVIDIA Parabricks genomics analysis pipelines with pre-configured templates for accelerated DNA/RNA sequencing workflows."
        }
      ]
    },
    {
      date: "July 29, 2025",
      sections: [
        {
          title: "AI as a Service Launch",
          content: "Our AI as a Service feature is now available. Users can access ready-to-use endpoints that are pre-deployed and ready for immediate integration. We're launching with the Typhoon-OCR model endpoint, priced at approximately $0.03 (or 1 THB) per request.\n\nIf you're interested in exploring this service or need assistance with integration, feel free to reach out at 087-660-4840 or email namfon.kha@float16.cloud."
        }
      ]
    },
    {
      date: "May 15, 2025",
      sections: [
        {
          title: "Billing Feature Launch",
          content: "Our Service billing feature is now available. Users can access services up to $100 per month with usage summaries provided on the 1st of each following month. For limit increases beyond $100, please contact us at 087-660-4840 or email namfon.kha@float16.cloud."
        }
      ]
    },
    {
      date: "March 10, 2025",
      sections: [
        {
          title: "Serverless GPU Service",
          content: "Our Serverless GPU service is now available, but we are still working on the billing feature. At this stage, you can explore the service with a budget limit of $2. \n\nIf you're interested in trying it out, feel free to reach out at 087-660-4840 or email namfon.kha@float16.cloud. Please note that the service is still in the testing phase, so performance may vary."
        },
        {
          title: "LLM as a Service & One-Click Deploy Update",
          content: "We are currently enhancing both LLM as a Service and One-Click Deploy in Production, so they are temporarily unavailable. Our team is working hard to bring them back as soon as possible. Thank you for your patience!"
        }
      ]
    }
  ];

  return (
    <>
      <div className="text-center">
        <h1 className="mb-6 text-2xl font-semibold">Changelog</h1>
        <p className="mx-auto max-w-lg">
          Stay updated with all the latest improvements and new features
          released on our platform. Never miss important updates and fixes.
        </p>
      </div>
      <div className="mx-auto mt-12 max-w-3xl space-y-10 border-l border-dashed border-border pl-6">
        {changelogEntries.map((entry, index) => (
          <div key={index}>
            <p className="relative font-mono text-sm text-muted-foreground">
              <time className="absolute -left-6 top-1 h-3 w-3 -translate-x-1/2 rounded-full bg-success"></time>
              {entry.date}
            </p>
            
            {entry.sections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <h2 className="mb-2 mt-4 text-xl font-semibold">
                  {section.title}
                </h2>
                <p className="prose dark:prose-invert text-primary/80 whitespace-pre-line">
                  {section.content}
                </p>
                {section.link && (
                  <a
                    href={section.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    {section.link.label}
                    <ArrowTopRightIcon className="size-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

export default Changelog;
